<template>
  <div>
    <InfoFormBlock
      :id="encodeHash($t('petitionForm.petitionAnchor') + '-' + props.data.formId)"
      :data="componentData"
    >
      <PetitionCounter
        :is-closed="!!document?.closed"
        :is-protest="isProtest"
        :form-id="document.dbOverride || props.data.formId"
        class="mb-4 lg:mb-10"
      />
      <FormPetition
        v-if="!document?.closed"
        :petition-id="props.data.formId"
        :db-override="document.dbOverride || ''"
        :is-protest="isProtest"
        :endpointId="document?.endpoint?.sys?.id || ''"
        :campaignCode="document?.actionCode || ''"
        :mailTemplate="document.mailTemplate"
        :mailTo="document.mailTo"
        :description="document?.excerpt"
      />
      <div
        v-if="document.closed && document.closedMessage"
        class="prose font-sans text-base leading-[21px] lg:prose-lg prose-p:text-lg prose-a:text-blue prose-a:no-underline hover:prose-a:text-blue-dark lg:leading-[26px] lg:prose-p:text-xl"
      >
        <div v-if="closedMessage" v-html="closedMessage" />
      </div>
    </InfoFormBlock>
    <PetitionMotivations
      v-if="!props.data.field_hide_motivations"
      :form-id="props.data.formId"
      :is-protest="isProtest"
      class="mb-4 lg:mb-10"
    />
  </div>
</template>

<script lang="ts" setup>
import type { ActionDocument } from '../../../types/contentfulCms'

import FormPetition from '@/components/molecules/FormPetition.vue'
import InfoFormBlock from './InfoFormBlock.vue'
import PetitionCounter from '@/components/molecules/PetitionCounter.vue'
import PetitionMotivations from '@/components/molecules/PetitionMotivations.vue'
import { useI18n } from '#imports'
import { marked } from 'marked'
import { encodeHash } from 'ufo'
import type { ContentfulClientApi } from 'contentful'
import { useRichText } from '~/composables/useRichText'
import type { Document } from '@contentful/rich-text-types'

const { $client } = useNuxtApp()
const { t } = useI18n()
const client = $client as ContentfulClientApi<undefined>

export interface Props {
  data: {
    formId: string
    field_hide_motivations: boolean
  }
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({ formId: '', field_hide_motivations: false }),
})

const { data: dataContentful } = await useAsyncData(`petition--${props.data.formId}`, () =>
  client.getEntries({
    content_type: 'action',
    'fields.slug': props.data.formId,
    limit: 1,
  }),
)

const document: Partial<ActionDocument> = dataContentful?.value?.items[0]?.fields ?? {}

const closedMessage = computed(() => useRichText((document?.closedMessage as Document) || ''))

const isProtest = document?.mailTemplate ? true : false

const componentData = computed(() => ({
  title: document?.name || '',
  subtitle: isProtest ? t('petitionForm.protestLabel') : t('petitionForm.petitionLabel'),
  copy: document?.excerpt ? marked(document.excerpt) : '',
  image: {
    url: 'https:' + document?.image?.fields?.file?.url,
    type: 'url',
    alt: document?.name || '',
  },
}))
</script>
